import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Wrench, Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

function formatErr(detail) {
  if (detail == null) return "Login failed. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return String(detail);
}

const Login = () => {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [sp] = useSearchParams();
  const next = sp.get("next") || "/account";
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      const u = await login(email, password);
      navigate(u.role === "admin" ? "/admin" : next);
    } catch (err) { setError(formatErr(err.response?.data?.detail) || err.message); }
    finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-white">
      <div className="hidden lg:flex flex-col justify-between p-12 relative overflow-hidden bg-graphite">
        <img src="https://images.pexels.com/photos/38136632/pexels-photo-38136632.jpeg" alt="" className="absolute inset-0 w-full h-full object-cover opacity-25" />
        <Link to="/" className="relative flex items-center gap-2"><div className="w-9 h-9 rounded-md bg-orange grid place-items-center"><Wrench className="w-5 h-5 text-white" /></div><span className="font-extrabold tracking-tight text-white text-lg">GOT PARTS</span></Link>
        <div className="relative"><h2 className="text-3xl font-bold text-white tracking-tight">Welcome back.</h2><p className="text-white/60 mt-3 max-w-sm">Sign in to place orders, request quotes and reorder your parts in seconds.</p></div>
        <div className="relative mono text-xs text-white/30">Industrial B2B Commerce</div>
      </div>

      <div className="grid place-items-center p-6">
        <form onSubmit={submit} className="w-full max-w-sm" data-testid="customer-login-form">
          <Link to="/" className="lg:hidden flex items-center gap-2 mb-6"><div className="w-8 h-8 rounded-md bg-graphite grid place-items-center"><Wrench className="w-4 h-4 text-orange" /></div><span className="font-extrabold tracking-tight text-graphite">GOT PARTS</span></Link>
          <h1 className="text-2xl font-bold tracking-tight text-graphite">Sign in</h1>
          <p className="text-sm text-steel-light mt-1 mb-6">Access your B2B account.</p>
          <div className="space-y-4">
            <div><Label htmlFor="email">Email</Label><Input id="email" data-testid="login-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1.5 h-11" required /></div>
            <div><Label htmlFor="password">Password</Label><Input id="password" data-testid="login-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="mt-1.5 h-11" required /></div>
            {error && <div data-testid="login-error" className="text-sm text-red-600 bg-red-50 border border-red-200 rounded px-3 py-2">{error}</div>}
            <Button type="submit" data-testid="login-submit" disabled={loading} className="w-full h-11 bg-orange hover:bg-orange-hover">{loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Sign In"}</Button>
          </div>
          <p className="text-sm text-steel-light mt-6 text-center">No account? <Link to="/register" data-testid="goto-register" className="text-orange font-medium hover:underline">Create one</Link></p>
        </form>
      </div>
    </div>
  );
};

export default Login;
